import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../core/services/api';
import { NotificationService } from '../../core/services/notification';
import { CustomValidators } from '../../shared/validators/custom-validators';

@Component({
    selector: 'app-contact',
    standalone: true,
    imports: [CommonModule, TranslateModule, ReactiveFormsModule],
    template: `
    <div class="bg-white py-12">
      <div class="container mx-auto px-4 max-w-4xl">
        <!-- Header -->
        <div class="text-center mb-16">
          <h1 class="text-4xl font-bold mb-4 uppercase text-enduro-black">{{ 'NAV.CONTACT' | translate }}</h1>
          <div class="w-24 h-1 bg-enduro-orange mx-auto mb-8"></div>
          <p class="text-gray-600 text-lg">
            {{ 'CONTACT_PAGE.DESCRIPTION' | translate }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
            <!-- Info Side -->
            <div class="bg-gray-50 p-8 rounded-xl h-fit">
                <h3 class="text-2xl font-bold mb-6 text-enduro-black">{{ 'CONTACT_PAGE.DIRECT_CONTACTS' | translate }}</h3>
                
                <div class="space-y-6">
                    <div class="flex items-start">
                        <div class="bg-enduro-orange bg-opacity-10 p-3 rounded-full mr-4 text-enduro-orange">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div>
                            <p class="font-bold text-gray-900">{{ 'CONTACT_PAGE.EMAIL' | translate }}</p>
                            <a href="mailto:enduromarghineexperience@gmail.com" class="text-gray-600 hover:text-enduro-orange">enduromarghineexperience@gmail.com</a>
                        </div>
                    </div>

                    <div class="flex items-start">
                        <div class="bg-enduro-orange bg-opacity-10 p-3 rounded-full mr-4 text-enduro-orange">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                        </div>
                        <div>
                            <p class="font-bold text-gray-900">{{ 'CONTACT_PAGE.PHONE' | translate }}</p>
                            <a href="tel:+393496253021" class="text-gray-600 hover:text-enduro-orange">+39 349 625 3021</a>
                        </div>
                    </div>

                    <div class="flex items-start">
                        <div class="bg-enduro-orange bg-opacity-10 p-3 rounded-full mr-4 text-enduro-orange">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <div>
                            <p class="font-bold text-gray-900">{{ 'CONTACT_PAGE.LOCATION' | translate }}</p>
                            <p class="text-gray-600">Via Manigos 5<br>Bolotana (NU) 08011<br>Italia</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Form Side -->
            <div>
                <h3 class="text-2xl font-bold mb-6 text-gray-800">{{ 'CONTACT_PAGE.WRITE_US' | translate }}</h3>
                <form [formGroup]="contactForm" (ngSubmit)="onSubmit()" class="space-y-4">
                    <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">
                            {{ 'CONTACT_PAGE.FORM.NAME' | translate }} <span class="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" 
                            formControlName="name"
                            [placeholder]="'CONTACT_PAGE.FORM.PLACEHOLDERS.NAME' | translate"
                            [class.border-red-500]="contactForm.get('name')?.invalid && contactForm.get('name')?.touched"
                            class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                        >
                        @if (contactForm.get('name')?.invalid && contactForm.get('name')?.touched) {
                            <p class="text-xs text-red-600 mt-1">
                                @if (contactForm.get('name')?.errors?.['required']) {
                                    {{ 'CONTACT_PAGE.FORM.ERRORS.NAME_REQUIRED' | translate }}
                                }
                                @if (contactForm.get('name')?.errors?.['minlength']) {
                                    {{ 'CONTACT_PAGE.FORM.ERRORS.NAME_LENGTH' | translate }}
                                }
                                @if (contactForm.get('name')?.errors?.['hasNumbers']) {
                                    {{ 'CONTACT_PAGE.FORM.ERRORS.NAME_NO_NUMBERS' | translate }}
                                }
                            </p>
                        }
                    </div>
                    
                    <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">
                            {{ 'CONTACT_PAGE.FORM.EMAIL' | translate }} <span class="text-red-500">*</span>
                        </label>
                        <input 
                            type="email" 
                            formControlName="email"
                            [placeholder]="'CONTACT_PAGE.FORM.PLACEHOLDERS.EMAIL' | translate"
                            [class.border-red-500]="contactForm.get('email')?.invalid && contactForm.get('email')?.touched"
                            class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                        >
                        @if (contactForm.get('email')?.invalid && contactForm.get('email')?.touched) {
                            <p class="text-xs text-red-600 mt-1">
                                @if (contactForm.get('email')?.errors?.['required']) {
                                    {{ 'CONTACT_PAGE.FORM.ERRORS.EMAIL_REQUIRED' | translate }}
                                }
                                @if (contactForm.get('email')?.errors?.['email']) {
                                    {{ 'CONTACT_PAGE.FORM.ERRORS.EMAIL_INVALID' | translate }}
                                }
                            </p>
                        }
                    </div>

                    <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">
                            {{ 'CONTACT_PAGE.FORM.MESSAGE' | translate }} <span class="text-red-500">*</span>
                        </label>
                        <textarea 
                            formControlName="message"
                            rows="4"
                            [placeholder]="'CONTACT_PAGE.FORM.PLACEHOLDERS.MESSAGE' | translate"
                            [class.border-red-500]="contactForm.get('message')?.invalid && contactForm.get('message')?.touched"
                            class="w-full rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                        ></textarea>
                        @if (contactForm.get('message')?.invalid && contactForm.get('message')?.touched) {
                            <p class="text-xs text-red-600 mt-1">
                                @if (contactForm.get('message')?.errors?.['required']) {
                                    {{ 'CONTACT_PAGE.FORM.ERRORS.MESSAGE_REQUIRED' | translate }}
                                }
                                @if (contactForm.get('message')?.errors?.['minlength']) {
                                    {{ 'CONTACT_PAGE.FORM.ERRORS.MESSAGE_LENGTH' | translate }}
                                }
                            </p>
                        }
                    </div>

                    <div class="flex justify-end">
                        <button 
                            type="submit" 
                            [disabled]="submitting() || contactForm.invalid"
                            [class.opacity-50]="submitting() || contactForm.invalid"
                            [class.cursor-not-allowed]="submitting() || contactForm.invalid"
                            class="inline-flex justify-center rounded-md bg-enduro-orange px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-opacity-90 transition-all w-full sm:w-auto uppercase tracking-wider"
                        >
                            {{ submitting() ? ('CONTACT_PAGE.FORM.SUBMITTING' | translate) : ('CONTACT_PAGE.FORM.SUBMIT' | translate) }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      </div>
    </div>
  `
})
export class ContactComponent {
    private fb = inject(FormBuilder);
    private api = inject(ApiService);
    private notification = inject(NotificationService);
    private translate = inject(TranslateService);

    submitting = signal(false);
    contactForm: FormGroup = this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2), CustomValidators.noNumbers]],
        email: ['', [Validators.required, Validators.email]],
        message: ['', [Validators.required, Validators.minLength(10)]]
    });

    onSubmit() {
        Object.keys(this.contactForm.controls).forEach(key => {
            this.contactForm.get(key)?.markAsTouched();
        });

        if (this.contactForm.invalid) {
            this.translate.get('CONTACT_PAGE.FORM.ERRORS.MISSING_FIELDS').subscribe(res => {
                this.notification.showError(res || 'Compila correttamente tutti i campi obbligatori');
            });
            return;
        }

        this.submitting.set(true);

        this.api.post('contact', this.contactForm.value).subscribe({
            next: () => {
                this.translate.get('CONTACT_PAGE.FORM.SUCCESS').subscribe(msg => {
                    this.notification.showSuccess(msg || 'Messaggio inviato con successo!');
                });
                this.contactForm.reset();
                this.submitting.set(false);
            },
            error: (err) => {
                console.error(err);
                // Extract backend error message
                const backendError = err.error?.error || '';

                if (backendError.includes('Invalid email domain')) {
                    this.translate.get('CONTACT_PAGE.FORM.ERRORS.INVALID_EMAIL_DOMAIN').subscribe(res => {
                        this.notification.showError(res);
                    });
                } else if (backendError.includes('Missing required fields')) {
                    this.translate.get('CONTACT_PAGE.FORM.ERRORS.MISSING_FIELDS').subscribe(res => {
                        this.notification.showError(res);
                    });
                } else if (backendError) {
                    this.notification.showError(backendError);
                } else {
                    this.translate.get('CONTACT_PAGE.FORM.ERRORS.GENERIC_ERROR').subscribe(msg => {
                        this.notification.showError(msg || 'Errore durante l\'invio. Riprova.');
                    });
                }

                this.submitting.set(false);
            }
        });
    }
}
